import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";

// Print Center (`/api/v1/printing/*`): printing catalog, received stock that
// needs labels, label-sheet presets and print jobs/history.

const unwrap = (res) => {
  if (!res) return null;
  if (typeof res === "object" && "success" in res) {
    return res.success ? res.data : null;
  }
  return res.data ?? res;
};

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

export const printingApi = createApi({
  reducerPath: "printingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) => prepareAuthHeaders(headers, { contentTypeJson: true }),
  }),
  tagTypes: ["PrintCatalog", "LabelDemand", "PrintJobs", "PrintJob", "PrintPresets"],
  // Stock and label demand change outside the Print Center (receiving, sales),
  // so everything is refetched when the page is opened again.
  keepUnusedDataFor: 30,
  refetchOnMountOrArgChange: true,

  endpoints: (builder) => ({
    fetchPrintCatalog: builder.query({
      query: (params) => ({ url: "printing/catalog", params: cleanParams(params) }),
      transformResponse: (res) => unwrap(res) || { items: [], total: 0, page: 1, totalPages: 1 },
      providesTags: [{ type: "PrintCatalog", id: "LIST" }],
    }),
    fetchPrintFacets: builder.query({
      query: () => "printing/catalog/facets",
      transformResponse: (res) =>
        unwrap(res) || { brands: [], categories: [], locations: [], colors: [], sizes: [] },
    }),
    resolvePrintBarcode: builder.query({
      query: (barcode) => ({ url: "printing/catalog/resolve-barcode", params: { barcode } }),
      transformResponse: (res) => unwrap(res) || { match: "none", item: null },
    }),
    resolvePrintItems: builder.mutation({
      query: (body) => ({ url: "printing/catalog/resolve-items", method: "POST", body }),
      transformResponse: (res) => unwrap(res) || { items: [], missingKeys: [], total: 0 },
    }),

    fetchLabelDemand: builder.query({
      query: (params) => ({ url: "printing/label-demand", params: cleanParams(params) }),
      transformResponse: (res) => unwrap(res),
      providesTags: [{ type: "LabelDemand", id: "LIST" }],
    }),
    fetchLabelDemandSummary: builder.query({
      query: () => "printing/label-demand/summary",
      transformResponse: (res) => unwrap(res) || { outstanding: 0, awaitingConfirmation: [] },
      providesTags: [{ type: "LabelDemand", id: "SUMMARY" }],
    }),
    // "Print X labels" links: what these receipts still need, recomputed now.
    resolveReceiptLabels: builder.mutation({
      query: (receiptIds) => ({ url: "printing/label-demand/receipts", method: "POST", body: { receiptIds } }),
      transformResponse: (res) => unwrap(res) || { receipts: [], items: [], notFound: [] },
    }),
    // Labels marked as not needed (audit trail, closed demand included).
    fetchLabelDismissals: builder.query({
      query: (params) => ({ url: "printing/label-demand/dismissals", params: cleanParams(params) }),
      transformResponse: (res) => unwrap(res),
      providesTags: [{ type: "LabelDemand", id: "DISMISSALS" }],
    }),
    dismissLabelDemand: builder.mutation({
      query: (body) => ({ url: "printing/label-demand/dismiss", method: "POST", body }),
      transformResponse: (res) => unwrap(res),
      invalidatesTags: [
        { type: "LabelDemand", id: "LIST" },
        { type: "LabelDemand", id: "SUMMARY" },
        { type: "LabelDemand", id: "DISMISSALS" },
        { type: "PrintCatalog", id: "LIST" },
      ],
    }),

    fetchPrintPresets: builder.query({
      query: () => "printing/presets",
      transformResponse: (res) => unwrap(res) || [],
      providesTags: [{ type: "PrintPresets", id: "LIST" }],
    }),
    updatePrintPreset: builder.mutation({
      query: ({ id, body }) => ({ url: `printing/presets/${id}`, method: "PUT", body }),
      transformResponse: (res) => unwrap(res),
      invalidatesTags: [{ type: "PrintPresets", id: "LIST" }],
    }),
    createPrintPreset: builder.mutation({
      query: (body) => ({ url: "printing/presets", method: "POST", body }),
      transformResponse: (res) => unwrap(res),
      invalidatesTags: [{ type: "PrintPresets", id: "LIST" }],
    }),
    archivePrintPreset: builder.mutation({
      query: (id) => ({ url: `printing/presets/${id}/archive`, method: "POST" }),
      transformResponse: (res) => unwrap(res),
      invalidatesTags: [{ type: "PrintPresets", id: "LIST" }],
    }),

    fetchPrintJobs: builder.query({
      query: (params) => ({ url: "printing/jobs", params: cleanParams(params) }),
      transformResponse: (res) => unwrap(res) || { items: [], total: 0, page: 1, totalPages: 1 },
      providesTags: [{ type: "PrintJobs", id: "LIST" }],
    }),
    fetchPrintJob: builder.query({
      query: (id) => `printing/jobs/${id}`,
      transformResponse: (res) => unwrap(res),
      providesTags: (_result, _error, id) => [{ type: "PrintJob", id }],
    }),
    createPrintJob: builder.mutation({
      query: (body) => ({ url: "printing/jobs", method: "POST", body }),
      transformResponse: (res) => unwrap(res),
      invalidatesTags: [
        { type: "PrintJobs", id: "LIST" },
        { type: "LabelDemand", id: "LIST" },
        { type: "LabelDemand", id: "SUMMARY" },
        { type: "PrintCatalog", id: "LIST" },
      ],
    }),
    // The stable product + colour QR link of each variant being printed (created on first use).
    resolveQrLinks: builder.mutation({
      query: (variantIds) => ({ url: "printing/qr-links", method: "POST", body: { variantIds } }),
      transformResponse: (res) => unwrap(res) || { enabled: false, links: {} },
    }),
    recordPrintAttempt: builder.mutation({
      query: ({ id, body }) => ({ url: `printing/jobs/${id}/attempts`, method: "POST", body }),
      transformResponse: (res) => unwrap(res),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PrintJob", id },
        { type: "PrintJobs", id: "LIST" },
      ],
    }),
    confirmPrintJob: builder.mutation({
      query: ({ id, body }) => ({ url: `printing/jobs/${id}/confirm`, method: "POST", body: body || {} }),
      transformResponse: (res) => unwrap(res),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PrintJob", id },
        { type: "PrintJobs", id: "LIST" },
        { type: "LabelDemand", id: "LIST" },
        { type: "LabelDemand", id: "SUMMARY" },
        { type: "PrintCatalog", id: "LIST" },
      ],
    }),
    discardPrintJob: builder.mutation({
      query: (id) => ({ url: `printing/jobs/${id}/discard`, method: "POST" }),
      transformResponse: (res) => unwrap(res),
      invalidatesTags: (_result, _error, id) => [
        { type: "PrintJob", id },
        { type: "PrintJobs", id: "LIST" },
        { type: "LabelDemand", id: "LIST" },
        { type: "LabelDemand", id: "SUMMARY" },
        { type: "PrintCatalog", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useFetchPrintCatalogQuery,
  useFetchPrintFacetsQuery,
  useLazyResolvePrintBarcodeQuery,
  useResolvePrintItemsMutation,
  useResolveReceiptLabelsMutation,
  useFetchLabelDismissalsQuery,
  useFetchLabelDemandQuery,
  useFetchLabelDemandSummaryQuery,
  useDismissLabelDemandMutation,
  useFetchPrintPresetsQuery,
  useUpdatePrintPresetMutation,
  useCreatePrintPresetMutation,
  useArchivePrintPresetMutation,
  useFetchPrintJobsQuery,
  useFetchPrintJobQuery,
  useLazyFetchPrintJobQuery,
  useCreatePrintJobMutation,
  useResolveQrLinksMutation,
  useRecordPrintAttemptMutation,
  useConfirmPrintJobMutation,
  useDiscardPrintJobMutation,
} = printingApi;
